/**
 * Tasks (S-A63, list form).
 *
 * Runs against the real `TaskViewSet`. Note that `assignee` is READ-ONLY on the task
 * serializer: handing work to somebody is an assignment, which notifies them, so it
 * goes through `POST /tasks/{id}/assign/` rather than a PATCH. Same for status —
 * `POST /tasks/{id}/status/`.
 */

import { qk, usePaginatedList, type TaskListItem } from '@legalcrm/api';
import { Badge, Button, Select, StatusPill, Table, statusTone, type Column } from '@legalcrm/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { STAFF } from '@legalcrm/api';
import { Can } from '@/auth/permissions';
import { formatDate, formatRelative, humanise } from '@/lib/format';
import { NewTaskModal } from '@/routes/NewTaskModal';
import { PageHeader } from '@/shell/PageHeader';

const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function TaskListPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);

  const list = usePaginatedList<TaskListItem>(qk.list('tasks'), STAFF.tasks, {
    status: status || undefined,
  });

  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [status, resetCursor]);

  const columns: Column<TaskListItem>[] = [
    {
      key: 'title',
      header: 'Task',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink">{row.title}</p>
          {row.subject_type && (
            <p className="truncate text-xs text-ink-muted">{humanise(row.subject_type)}</p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      hideOnMobile: true,
      render: (row) => (
        <StatusPill label={humanise(row.status)} tone={statusTone(row.status)} />
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      hideOnMobile: true,
      render: (row) => (
        <Badge tone={row.priority === 'urgent' ? 'danger' : 'neutral'} size="sm">
          {humanise(row.priority)}
        </Badge>
      ),
    },
    {
      key: 'due_at',
      header: 'Due',
      align: 'right',
      render: (row) =>
        row.due_at ? (
          <div>
            <p className={row.is_overdue ? 'font-medium text-danger' : 'text-ink'}>
              {row.is_overdue ? 'Overdue' : formatRelative(row.due_at)}
            </p>
            <p className="text-xs text-ink-subtle">{formatDate(row.due_at)}</p>
          </div>
        ) : (
          <span className="text-ink-subtle">—</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Tasks"
        description="Work assigned to you and to matters you are on."
        actions={
          <Can module="task" action="create">
            <Button variant="primary" onClick={() => setCreating(true)}>
              New task
            </Button>
          </Can>
        }
      />

      <NewTaskModal open={creating} onClose={() => setCreating(false)} />

      <div className="mb-3 max-w-xs">
        <Select
          label="Status"
          hideLabel
          options={STATUS_OPTIONS}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        />
      </div>

      <Table
        caption="Task list"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        // Opens the task. It used to jump to the matter the task hung off, which
        // made a task attached to nothing unopenable and made every other task
        // look like it had the wrong screen.
        onRowClick={(row) => navigate(`/tasks/${row.id}`)}
        emptyTitle="Nothing outstanding"
        emptyDescription="No tasks match this filter."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
        }}
      />
    </>
  );
}
